/**
 * Sidebar content: logo area, country identity block, and navigation items.
 * Used by both the mobile temporary drawer and the desktop permanent sidebar.
 *
 * Navigation sections are driven by the selected functionality (from appStore)
 * combined with role-based filtering. Admin sections render at top for admins.
 * Static sections (Company, Community, Help) are pinned to the sidebar bottom
 * with a visual divider, separate from the scrollable functionality nav.
 */

import { useState } from 'react';
import { Box, List } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ifrs17Policies } from '../../api/endpoints';
import { useAuthStore } from '../../stores/authStore';
import { useAppStore } from '../../stores/appStore';
import { COLORS, FUNCTIONALITY_NAV, SIDEBAR_WIDTH } from './layoutConstants';
import {
  SidebarHeader,
  AdminNav,
  FunctionalityNav,
  SidebarBottomNav,
} from './sidebar';

// ─── Component ───────────────────────────────────────────────────────────────

interface SidebarContentProps {
  sidebarExpanded: boolean;
  isMobile: boolean;
  currentWidth: number;
  onDrawerToggle: () => void;
  onMobileNavClick: () => void;
}

export default function SidebarContent({
  sidebarExpanded,
  isMobile,
  currentWidth,
  onDrawerToggle,
  onMobileNavClick,
}: SidebarContentProps) {
  const user = useAuthStore((s) => s.user);
  const selectedFunctionality = useAppStore((s) => s.selectedFunctionality);
  const selectedYear = useAppStore((s) => s.selectedYear);

  // ── 7-tier RBAC role derivation ──────────────────────────────────────
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isCountryAdmin = user?.role === 'admin';
  const isRegulator = user?.role === 'regulator';
  const hasCompany =
    user?.role === 'company_user' ||
    user?.role === 'consultant_user' ||
    user?.role === 'auditor' ||
    user?.role === 'executive';

  // ── IFRS 4 Balance Sheet visibility (conditioned on policy toggle) ──
  const { data: allPolicies } = useQuery({
    queryKey: ['ifrs17Policies'],
    queryFn: () => ifrs17Policies.getAll(),
    staleTime: 10 * 60 * 1000,
    enabled: hasCompany,
  });
  const currentPolicy = allPolicies?.find((p) => p.year === selectedYear);
  const ifrs4Enabled = currentPolicy?.ifrs4_balance_sheet_enabled ?? false;

  // ── Collapse state per section label (persists across functionality switches) ─
  const BOTTOM_SECTIONS = new Set(['Company', 'Community', 'Help']);
  const [collapseStates, setCollapseStates] = useState<Record<string, boolean>>({});
  const isSectionOpen = (label: string) =>
    collapseStates[label] ?? (BOTTOM_SECTIONS.has(label) ? false : true);
  const toggleSection = (label: string) => {
    const defaultOpen = BOTTOM_SECTIONS.has(label) ? false : true;
    setCollapseStates((prev) => ({ ...prev, [label]: !(prev[label] ?? defaultOpen) }));
  };

  // ── Resolve functionality nav sections ──
  const sections = FUNCTIONALITY_NAV[selectedFunctionality] ?? FUNCTIONALITY_NAV['audit_workbook'];
  const userRole = user?.role ?? '';

  return (
    <Box
      sx={{
        width: isMobile ? SIDEBAR_WIDTH : currentWidth,
        backgroundColor: COLORS.sidebarBg,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        transition: isMobile ? 'none' : 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <SidebarHeader
        sidebarExpanded={sidebarExpanded}
        isMobile={isMobile}
        onDrawerToggle={onDrawerToggle}
        isCountryAdmin={isCountryAdmin}
        countryName={user?.country_name}
        countryFlagEmoji={user?.country_flag_emoji}
      />

      {/* ── Primary scrollable nav area ─────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          pt: 1,
          pb: 1,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: COLORS.sidebarBorder,
            borderRadius: 2,
          },
        }}
      >
        <List component="nav" disablePadding>
          {isAdmin && (
            <AdminNav
              sidebarExpanded={sidebarExpanded}
              isSuperAdmin={isSuperAdmin}
              isCountryAdmin={isCountryAdmin}
              isSectionOpen={isSectionOpen}
              toggleSection={toggleSection}
              onMobileNavClick={onMobileNavClick}
            />
          )}

          {!isAdmin && (
            <FunctionalityNav
              sidebarExpanded={sidebarExpanded}
              isRegulator={isRegulator}
              sections={sections}
              userRole={userRole}
              ifrs4Enabled={ifrs4Enabled}
              isSectionOpen={isSectionOpen}
              toggleSection={toggleSection}
              onMobileNavClick={onMobileNavClick}
            />
          )}
        </List>
      </Box>

      <SidebarBottomNav
        sidebarExpanded={sidebarExpanded}
        isAdmin={isAdmin}
        isRegulator={isRegulator}
        hasCompany={hasCompany}
        userRole={userRole}
        isSectionOpen={isSectionOpen}
        toggleSection={toggleSection}
        onMobileNavClick={onMobileNavClick}
      />
    </Box>
  );
}
